import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createChart, LineSeries } from 'lightweight-charts';
import { observeChartSize } from '../utils/chartResize';
import { fetchMacroSeries } from '../utils/bondMacroApi';
import {
  computeYieldCurveSpread,
  formatSlopeRange,
  macroDateYearsAgo,
  monthlyMeanBars,
  zeroLineFromSpread,
} from '../utils/yieldCurveSlope';
import {
  classifyPointAtTime,
  classifyYieldCurveSignal,
  formatSignalMomentumLine,
  latestInversionSpell,
} from '../utils/yieldCurveSignal';
import YieldCurveSlopeHelp from './YieldCurveSlopeHelp';
import './BondDashboard.css';
import './YieldCurveSlopePanel.css';

const COLOR_SPREAD = '#1763A1';
const COLOR_ZERO = '#E07020';
const YEARS = 10;

function toChartData(bars) {
  return (bars || []).map((b) => ({ time: b.time, value: Number(b.value) }));
}

function formatMonth(iso) {
  const [y, m] = iso.split('-');
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  return `${months[Number(m) - 1]} ${y}`;
}

function YieldCurveSlopePanel() {
  const chartRef = useRef(null);
  const containerRef = useRef(null);
  const spreadSeriesRef = useRef(null);
  const zeroSeriesRef = useRef(null);
  const spreadBarsRef = useRef([]);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [spreadBars, setSpreadBars] = useState([]);
  const [hoverTip, setHoverTip] = useState(null);

  const rangeLabel = useMemo(() => formatSlopeRange(spreadBars), [spreadBars]);
  const currentSignal = useMemo(
    () => classifyYieldCurveSignal(spreadBars),
    [spreadBars],
  );
  const inversionSpell = useMemo(() => latestInversionSpell(spreadBars), [spreadBars]);

  useEffect(() => {
    spreadBarsRef.current = spreadBars;
  }, [spreadBars]);

  useEffect(() => {
    if (!containerRef.current || chartRef.current) return undefined;

    const chart = createChart(containerRef.current, {
      layout: { background: { color: '#FFFBF3' }, textColor: '#16244A' },
      grid: { vertLines: { color: '#EDE3CF' }, horzLines: { color: '#EDE3CF' } },
      crosshair: { mode: 1 },
      rightPriceScale: { borderColor: '#E3D9C6' },
      timeScale: { borderColor: '#E3D9C6', timeVisible: true, secondsVisible: false },
      width: containerRef.current.clientWidth,
      height: 320,
    });

    spreadSeriesRef.current = chart.addSeries(LineSeries, {
      color: COLOR_SPREAD,
      lineWidth: 2,
      priceLineVisible: false,
      lastValueVisible: true,
      title: '10y − 2y',
    });
    zeroSeriesRef.current = chart.addSeries(LineSeries, {
      color: COLOR_ZERO,
      lineWidth: 2,
      priceLineVisible: false,
      lastValueVisible: false,
      title: 'Zero',
    });

    chartRef.current = chart;

    const onCrosshairMove = (param) => {
      if (!param.time || param.point == null) {
        setHoverTip(null);
        return;
      }
      const signal = classifyPointAtTime(spreadBarsRef.current, param.time);
      if (!signal) {
        setHoverTip(null);
        return;
      }
      setHoverTip({
        x: param.point.x,
        y: param.point.y,
        signal,
      });
    };

    chart.subscribeCrosshairMove(onCrosshairMove);

    const disconnect = observeChartSize(containerRef.current, (width) => {
      chart.applyOptions({ width, height: 320 });
    });

    return () => {
      chart.unsubscribeCrosshairMove(onCrosshairMove);
      disconnect();
      chart.remove();
      chartRef.current = null;
      spreadSeriesRef.current = null;
      zeroSeriesRef.current = null;
      setHoverTip(null);
    };
  }, []);

  useEffect(() => {
    let cancelled = false;

    async function load() {
      setLoading(true);
      setError(null);
      const { from, to } = macroDateYearsAgo(YEARS);
      try {
        const [d10, d2] = await Promise.all([
          fetchMacroSeries('DGS10', { from, to }),
          fetchMacroSeries('DGS2', { from, to }),
        ]);
        if (cancelled) return;
        const spread = computeYieldCurveSpread(
          monthlyMeanBars(d10.bars),
          monthlyMeanBars(d2.bars),
        );
        if (!spread.length) {
          throw new Error('No overlapping DGS10/DGS2 observations — run macro backfill on investing backend');
        }
        setSpreadBars(spread);
      } catch (err) {
        if (!cancelled) {
          setError(err.message || 'Failed to load yield curve slope');
          setSpreadBars([]);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    load();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!spreadSeriesRef.current) return;
    const spreadData = toChartData(spreadBars);
    const zeroData = toChartData(zeroLineFromSpread(spreadBars));
    spreadSeriesRef.current.setData(spreadData);
    zeroSeriesRef.current?.setData(zeroData);
    chartRef.current?.timeScale().fitContent();
  }, [spreadBars]);

  const momentumLine = formatSignalMomentumLine(currentSignal);

  return (
    <section className="bond-dash-panel bond-dash-panel--fill yield-slope-panel">
      <div className="bond-dash-header yield-slope-header">
        <div className="yield-slope-header-text">
          <h3 className="bond-dash-title">
            US yield curve slope: 10y − 2y (monthly avg)
            {rangeLabel ? `, ${rangeLabel}` : ''}
          </h3>
          <p className="bond-dash-subtitle">
            FRED DGS10 minus DGS2. Hover the chart for the signal at that date. Orange line = inversion.
          </p>
        </div>
        <YieldCurveSlopeHelp />
      </div>

      <div className="yield-slope-signal-row">
        {currentSignal.spread != null && (
          <>
            <span className={`yield-slope-regime ${currentSignal.cssClass}`}>
              {currentSignal.shortLabel}
            </span>
            <span className="yield-slope-momentum">{momentumLine}</span>
            <span className="yield-slope-level">
              Spread {currentSignal.spread.toFixed(2)} pp
              {currentSignal.inverted ? ' · inverted' : ''}
            </span>
          </>
        )}
        {inversionSpell && (
          <span className="yield-slope-inversion-spell">
            Last inversion spell: {formatMonth(inversionSpell.from)}
            {' – '}
            {formatMonth(inversionSpell.to)}
          </span>
        )}
      </div>

      <div className="yield-slope-legend">
        <span className="yield-slope-legend-item spread">10y − 2y spread</span>
        <span className="yield-slope-legend-item zero">Zero (inversion line)</span>
      </div>

      {currentSignal.summary && currentSignal.id !== 'unknown' && (
        <p className="yield-slope-summary">{currentSignal.summary}</p>
      )}

      {loading && <div className="bond-dash-loading">Loading DGS10 &amp; DGS2…</div>}
      {error && <div className="bond-dash-error">{error}</div>}

      <div className="yield-slope-chart-wrap">
        <div ref={containerRef} className="yield-slope-chart" aria-label="Yield curve slope chart" />
        {hoverTip && (
          <div
            className="yield-slope-tooltip"
            style={{ left: hoverTip.x + 14, top: hoverTip.y + 14 }}
            role="tooltip"
          >
            <div className="yield-slope-tooltip-date">{formatMonth(hoverTip.signal.time)}</div>
            <div className="yield-slope-tooltip-spread">
              {hoverTip.signal.spread.toFixed(2)} pp
              {hoverTip.signal.inverted ? ' · inverted' : ''}
            </div>
            <div className={`yield-slope-tooltip-regime ${hoverTip.signal.cssClass}`}>
              {hoverTip.signal.shortLabel}
            </div>
            <div className="yield-slope-tooltip-mom">{formatSignalMomentumLine(hoverTip.signal)}</div>
            <p className="yield-slope-tooltip-body">{hoverTip.signal.summary}</p>
          </div>
        )}
        <span className="yield-slope-y-label">Spread (percentage points)</span>
      </div>
    </section>
  );
}

export default YieldCurveSlopePanel;
